/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2016, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define(['../../lib/@waca/core-client/js/core-client/ui/core/View',
	'text!./templates/Metadata.html',
	'../../lib/@waca/core-client/js/core-client/utils/BidiUtil',
	'../../lib/@waca/loading-indicator/src/js/LoadingIndicatorView',
	'../../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'../nls/StringResources',
	'../utils/ShapingUIUtils',
	'../../lib/@waca/dashboard-common/dist/lib/@ba-ui-toolkit/ba-graphics/dist/illustrations-js/unknown-warning_128',
	'react-dom',
	'react'
], function(View, DataSourceTitleTemplate, BidiUtil, LoadingIndicator, KeyCodes, StringResources, ShapingUIUtils, em_unknown_warning, ReactDOM, React) {

	return class Metadata extends View{
		constructor(...args) {
			super(...args);
		}

		init (...args) {
			const options = args[0];
			this.id = options.id;
			//Require to set this.templateString before call the parent class
			this.templateString = DataSourceTitleTemplate;

			super.init(...args);

			this.isVisible = true;
			this.dashboardApi = options.dashboardApi;
			this.dataSource = options.dataSource;
			this.panelController = options.panelController;
			//Workaround for now to make sure DataSource is initialized when Metadata is loaded
			this.dashboardApi.getFeature('DataSources').getDataSource(this.id);

			//handle the change of state(ready, loading) by showing or hiding
			//the loading icon
			this.changeStateHandler = this.dataSource.on('change:state', this.onSourceChangeState.bind(this));
			this.removeHandler = this.dataSource.on('remove', this.onSourceRemoved.bind(this));

			// Add dataSource title division
			const sHTML = this.dotTemplate({});
			this.$el.append(sHTML);
			const loadingIndicator = new LoadingIndicator();
			loadingIndicator.render()
				.then($el => {
					this.$el.find('.loading-indicator').append($el);
				});

			this.$el.on('keydown.metadatatreeKeydown', this.onKeyDown.bind(this));
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');
		}

		onKeyDown (event) {
			//CTRL+C to copy selection tree items
			if (event.keyCode === 67 && event.ctrlKey) {
				this._copySelectedTreeItems();
				return false;
			}
		}

		_copySelectedTreeItems () {
			return this.dataSource.getModule().then(function(module){
				ShapingUIUtils.copySelectedTreeItems(module);
			}.bind(this));
		}

		show (options) {
			this.isVisible = true;
			this.render(options);
		}

		render  (options) {
			this.dashboardApi.getFeature('DataSources').setActiveDataSourceId(this.dataSource.getId());
			if (!this.isRendered || (options && options.forceRefreshMetadata)) { //TODO: Hook up forceRefreshMetadata where necessary, and have it actually force a refresh
				this.isRendered = true;
			}

			this.panelController.clearIcons();
			this.panelController.renderDataSourcePaneButtons('com.ibm.bi.dashboard.dataSourcePanel.metadata.buttons').then(function() {
				if (this.isVisible && options && options.setFocus) {
					var firstButton = this.panelController.$el.find('.buttons .ds_btn');

					if (firstButton.length) {
						firstButton[0].focus();
					}
				}
			}.bind(this));
			return this._renderTree();
		}

		_renderTree () {
			if (!this._module) {
				return this.dataSource.getModule().then(function(module) {
					this._module = module;
					// It's possible we got a module back but we're still in an error case.
					// For example, if we have an empty dataset
					if (this.dataSource.getState() === 'error') {
						this.showError(StringResources.get('errorLoadingDataTree'));
						return Promise.resolve();
					} else {
						this._treeContainer = this.$el.find('.metadataContainer')[0];
						return ShapingUIUtils.renderTree(module, this.dashboardApi, this._treeContainer, this.addDataItemsOrAddWidget.bind(this));
					}
				}.bind(this), function(err) {
					this.showError(StringResources.get('errorLoadingDataTree'));
					throw err;
				}.bind(this));
			} else {
				return ShapingUIUtils.renderTree(this._module, this.dashboardApi, this._treeContainer, this.addDataItemsOrAddWidget.bind(this));
			}
		}

		showError (error) {
			var $container = this.$el.find('.metadataContainer');
			var errorSourcesLabel = StringResources.get('errorSourcesTitle');
			this.reactDomNode = $container[0];
			ReactDOM.render(
				<div className="metadataTreeErrorContentWrapper">
					<div className="metadataTreeErrorImage ba-theme-waca">
						<svg viewBox={em_unknown_warning.default.viewBox} focusable="false">
							<use className="ba-graphics-themable" xlinkHref={'#'+em_unknown_warning.default.id} fill="#8ee9d4"/>
						</svg>
					</div>
					<div className="metadataTreeErrorText line1" role="option" title={errorSourcesLabel} aria-label={errorSourcesLabel}>{errorSourcesLabel}</div>
					<div className="metadataTreeErrorText line2" role="option" title={error} aria-label={error}>{error}</div>
				</div>,
				this.reactDomNode
			);
		}

		searchTypeDelay (evt, searchTerm) {
			this._renderTree(searchTerm);
		}

		addDataItemsOrAddWidget (payload, key) {
			if (key.shiftKey && key.keyCode === KeyCodes.RIGHT_ARROW) {
				this.addToCanvas(payload);
			} else if (key.shiftKey && key.altKey && key.keyCode === KeyCodes.F) {
				this.addToFilter(payload, true);
			} else if (key.shiftKey && key.keyCode === KeyCodes.F) {
				this.addToFilter(payload, false);
			}
		}

		addToCanvas  (payload) {
			if (payload.data && payload.data.utils && payload.data.utils.isValid()) {
				this.dashboardApi.addContentToCanvas(payload);
			}
		}

		addToFilter  (payload, globalScope) {
			const selectedColumns = payload.data.columns;
			let options =  {
				scope: null,
				openViewOnLoad: (selectedColumns.length > 1) ? false : true
			};
			let canvas = this.dashboardApi.getCanvas();

			return canvas.getFeature('filterDock').then((filterDock) => {
				if (globalScope && filterDock.isGlobalDockShown === false) {
					// if user is trying to add filter to global tab, ensure to see if
					// global tab is enabled or not. If not then don't do anything
					return;
				} else if (!globalScope) {
					// if user is trying to add filter to current tab, then use
					// the correct scope
					options.scope = filterDock.currentScope();
				}

				// loop through selected items and create filter for each
				for (let column of selectedColumns) {
					const metadataColumn = column.metadataColumn;
					if (this._getPageContextType(metadataColumn.getDataType(), metadataColumn.getType()) === 'range') {
						options.condition = {};
					} else {
						options.values = [];
					}
					canvas.getFilters().addFilter({
						sourceId: metadataColumn.getSourceId(),
						itemId: metadataColumn.getId()
					}, options);
				}
			});
		}

		_getPageContextType (dataType, usage) {
			if (usage === 'fact') {
				return 'range';
			}

			// usage is attribute: non numeric, tree, or date time filter
			var isDateType = dataType === 'date' || dataType === 'time' || dataType === 'datetime' || dataType === 'timestamp';
			return isDateType ? 'range' : 'tupleset';
		}

		onSourceChangeState () {
			if (this.isVisible) {
				this.panelController.setTypeIcon(this.dataSource);
			}
		}

		onSourceRemoved (event) {
			if (this.isVisible) {
				// If the source is removed by UndoRedoController, we don't want to render the dataSourceList after going back.
				// This is already taken care by DataSourceList.dataSourceCollection's 'on remove' handler.
				const preventRender = event && event.sender === 'UndoRedoController' ? true : false;
				this.panelController.goBack(preventRender);
			}
		}

		detach () {
			this.$el.detach();
			this.isVisible = false;
		}

		hide () {
			this.$el.hide();
			this.isVisible = false;
		}

		remove () {
			this.changeStateHandler.remove();
			this.removeHandler.remove();
			this.changeStateHandler = null;
			this.removeHandler = null;
			this._module = null;
			this.dashboardApi = null;
			this.dataSource = null;
			this.panelController = null;
			this.logger = null;
			if (this._treeContainer) {
				ReactDOM.unmountComponentAtNode(this._treeContainer);
			}
			Metadata.inherited('remove', this, arguments);
		}
	};
});
